import React from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { connect } from 'react-redux';
import Fa from 'react-fontawesome';

import MoneyDisplay from '../../../../components/money-display';
import NumberDisplay from '../../../../components/number-display';
import Counter from '../../../../components/counter';
import styles from './opportunity-graphs.css';
import { getNameFromKey, getAliasFromKey } from '../../../../util/helpers';
import { getHasNoDecisionSetup } from '../../../../components/search-form/search-form-reducer';
import { getOverview, getLoaded, getLoading } from '../../AnalysisReducer';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';


function overview(props) {
  const {
    stats, hasNoDecisionSetup, organization, loaded, loading,
  } = props;
  if (!loaded || loading) {
    return null;
  }
  const types = ['won', 'lost'];
  if (hasNoDecisionSetup) {
    types.push('noDecision');
  }
  let hasBigNumbers = false;
  let suffix = '';
  let symbol = '';
  if (organization) {
    suffix = organization.currency_suffix || '';
    symbol = organization.currency_symbol || '';
  }
  const amountStr = stats.amount ? stats.amount.toString() : '0';
  const extraLength = suffix.length + symbol.length;
  const maxLength = 8;
  const amountTips = {
    won: (<Tooltip id="won-amount-popover"><span>Your win rate based on deal amounts.</span></Tooltip>),
    lost: (<Tooltip id="lost-amount-popover"><span>Your loss rate based on deal amounts.</span></Tooltip>),
    noDecision: (<Tooltip id="no-decision-amount-popover"><span>Your no decision rate based on deal amounts.</span></Tooltip>),
  };
  const countTips = {
    won: (<Tooltip id="won-count-popover"><span>Your win rate based on deal counts.</span></Tooltip>),
    lost: (<Tooltip id="lost-count-popover"><span>Your loss rate based on deal counts.</span></Tooltip>),
    noDecision: (<Tooltip id="no-decision-count-popover"><span>Your no decision rate based on deal counts.</span></Tooltip>),
  };

  if (amountStr.length + extraLength > maxLength) {
    hasBigNumbers = true;
  } else {
    types.map((t) => {
      const aStr = stats[`${t}Amount`] ? stats[`${t}Amount`].toString() : '0';
      if (aStr.length + extraLength > maxLength) {
        hasBigNumbers = true;
      }
      return t;
    });
  }

  const hasBigNumberCls = hasBigNumbers ? styles.hasBigNumbers : '';
  return (
    <div className={`container ${styles.customContainer}`}>
      <div>
        <div className="row">
          <div className={hasNoDecisionSetup ? 'col-lg-3' : 'col-lg-4'}>
            <div className={`${styles.panel} ${hasBigNumberCls} panel panel-default animated flipInY`}>
              <div className="panel-body">
                <h3 className={`${styles.statHeading} clearfix`}>
                  <div className="pull-left"><strong><Counter value={stats.count}><NumberDisplay precision={0} suffix="deal" suffixIsUnit /></Counter></strong></div>
                  <div className="pull-right">AVG: <strong><Counter value={stats.average}><MoneyDisplay precision={1} abbreviated value={stats.average} /></Counter></strong></div>
                </h3>

                <div className={styles.counter}>
                  <Counter value={stats.amount}>
                    <MoneyDisplay precision={0} value={stats.amount} abbreviated={hasBigNumbers} />
                  </Counter>
                </div>
                <table className="table table-condensed">
                  <thead>
                    <tr>
                      <th className={styles.percentageHeading}>
                        Range:
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={styles.percentage}>
                        <Counter value={stats.min}>
                          <MoneyDisplay precision={1} value={stats.min} abbreviated />
                        </Counter>
                        &nbsp;-&nbsp;
                        <Counter value={stats.max}>
                          <MoneyDisplay precision={1} value={stats.max} abbreviated />
                        </Counter>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="clearfix hidden-lg"><p /></div>
          <div className={hasNoDecisionSetup ? 'col-lg-9' : 'col-lg-8'}>
            <div className="row">
              {types.map((type) => {
                const count = stats[`${type}Count`];
                const amount = stats[`${type}Amount`];
                const countRate = stats[`${type}CountRate`];
                const amountRate = stats[`${type}AmountRate`];
                const average = stats[`${type}Average`];
                const name = getNameFromKey(type);
                const alias = getAliasFromKey(type);
                const cls = styles[`panel${name.replace(' ', '')}`];
                const amountTip = amountTips[type] || null;
                const countTip = countTips[type] || null;

                return (
                  <div key={type} className={hasNoDecisionSetup ? 'col-sm-4' : 'col-sm-6'}>
                    <div className={`${styles.panel} ${cls} ${hasBigNumberCls} panel panel-default animated flipInY`}>
                      <div className="panel-body">
                        <h3 className={`${styles.statHeading} clearfix`}>
                          <div className="pull-left"><strong><Counter value={count}><NumberDisplay precision={0} /></Counter></strong> {alias}</div>
                          <div className="pull-right">AVG: <strong><Counter value={average}><MoneyDisplay precision={1} abbreviated value={average} /></Counter></strong></div>
                        </h3>
                        <div className={styles.counter}>
                          <Counter value={amount}>
                            <MoneyDisplay precision={hasBigNumbers ? 1 : 0} value={amount} abbreviated={hasBigNumbers} />
                          </Counter>
                        </div>
                        <table className="table table-condensed">
                          <thead>
                            <tr>
                              <th className={styles.percentageHeading}>
                                Amount&nbsp;Rate:
                              </th>
                              <th className={styles.percentageHeading}>
                                Count&nbsp;Rate:
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className={styles.percentage}>
                                {amountTip !== undefined &&
                                  <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={amountTip}>
                                    <span>
                                      <Counter value={amountRate}><NumberDisplay value={amountRate} precision={1} suffix="%" /></Counter> <Fa className={styles.info} name="info-circle" />
                                    </span>
                                  </OverlayTrigger>
                                }
                              </td>
                              <td className={styles.percentage}>
                                {countTip !== undefined &&
                                  <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={countTip}>
                                    <span>
                                      <Counter value={countRate}><NumberDisplay value={countRate} precision={1} suffix="%" /></Counter> <Fa className={styles.info} name="info-circle" />
                                    </span>
                                  </OverlayTrigger>
                                }
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

overview.propTypes = {
  stats: PropTypes.shape({}).isRequired,
  organization: PropTypes.shape({}),
  hasNoDecisionSetup: PropTypes.bool.isRequired,
  loaded: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
};

overview.defaultProps = {
  organization: null,
};

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    stats: getOverview(state),
    organization: getUserCurrentOrganization(state),
    loaded: getLoaded(state),
    loading: getLoading(state),
    hasNoDecisionSetup: getHasNoDecisionSetup(state),
  };
}

export default connect(mapStateToProps)(overview);
